/**
 * GTFS Feed Exporter (Fixed Routes → GTFS)
 *
 * Generates a spec-compliant GTFS `.zip` from the Fixed Routes state. This is
 * the inverse of `gtfsConverter.ts`:
 *   - Imported routes carry exact `tripSchedules` → emitted verbatim.
 *   - Atlas-authored routes have trips SYNTHESISED from `startTime`/
 *     `endTime` + `segments` (× trafficMultiplier) + per-stop `waitTimeMinutes`
 *     + circulator/return rules — the same loop/vehicle expansion implemented
 *     in `timetableUtils.generateTimetable`, but computed in ABSOLUTE minutes
 *     (no `% 24` wrap) so after-midnight service is encoded as `25:30:00`.
 *
 * Round-trip metadata (route_type, agency, route_desc, trip_headsign) captured
 * at import is written back; authored routes fall back to defaults (Bus, the
 * export modal's agency, last-stop headsign).
 *
 * Runs entirely in the browser (jszip + papaparse).
 */

import type {
  FixedRoute,
  FixedRouteStop,
  FixedRoutesState,
  GtfsAgency,
  ServiceDaysMask,
} from '../../types/fixedRoutes';
import type {
  GTFSAgencyRow,
  GTFSCalendarDateRow,
  GTFSCalendarRow,
  GTFSRoutesRow,
  GTFSShapeRow,
  GTFSStopsRow,
  GTFSStopTimeRow,
  GTFSTripRow,
} from './gtfsConverter';
import { GTFS_EXPORT_FILES } from './gtfsZipUtils';

/** GTFS `feed_info.txt` row (not present in `gtfsConverter` since import ignores it). */
export interface GTFSFeedInfoRow {
  feed_publisher_name: string;
  feed_publisher_url: string;
  feed_lang: string;
  feed_version?: string;
  feed_start_date?: string;
  feed_end_date?: string;
}

/** Default GTFS `route_type` for authored routes: 3 = Bus. */
export const DEFAULT_GTFS_ROUTE_TYPE = 3;

/** Feed-level metadata collected in the export modal. */
export interface GtfsFeedMeta {
  /** Used for the download filename and `feed_publisher_name`. */
  feedName: string;
  /** Default agency applied to routes with no preserved `gtfsAgency`. */
  agencyName: string;
  agencyUrl: string;
  /** IANA zone (e.g. `Australia/Perth`), derived from network settings / browser. */
  agencyTimezone: string;
  agencyLang: string;
  feedPublisherUrl?: string;
  /** Build/version stamp; pass in (do not compute — date APIs are unavailable in some contexts). */
  feedVersion?: string;
}

export interface GtfsExportOptions {
  /** Route ids to export. Omit/empty ⇒ all routes in the state. */
  selectedRouteIds?: string[];
  /** Fallback service days for routes with no `serviceDays` of their own. */
  defaultServiceDays: ServiceDaysMask;
  /** Fallback service date range (`YYYY-MM-DD` or `YYYYMMDD`). */
  defaultServiceDateRange: { startDate: string; endDate: string };
  /** Default `route_type` for authored routes (defaults to Bus). */
  defaultRouteType?: number;
  meta: GtfsFeedMeta;
}

/** A fully-assembled GTFS feed, ready to serialise to a `.zip`. */
export interface GtfsExportFeed {
  agency: GTFSAgencyRow[];
  stops: GTFSStopsRow[];
  routes: GTFSRoutesRow[];
  trips: GTFSTripRow[];
  stopTimes: GTFSStopTimeRow[];
  calendar: GTFSCalendarRow[];
  calendarDates: GTFSCalendarDateRow[];
  shapes: GTFSShapeRow[];
  feedInfo: GTFSFeedInfoRow[];
}

export interface SkippedRoute {
  routeId: string;
  routeName: string;
  reason: string;
}

export interface GtfsExportResult {
  feed: GtfsExportFeed;
  warnings: string[];
  skippedRoutes: SkippedRoute[];
  counts: {
    routes: number;
    trips: number;
    stops: number;
    calendars: number;
    agencies: number;
  };
}

const DAY_KEYS: Array<keyof ServiceDaysMask> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DEFAULT_END_HOUR = 22; // mirrors timetableUtils.generateTimetable default
const MAX_LOOPS = 200; // safety cap, mirrors generateTimetable

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const bool01 = (value: boolean): '0' | '1' => (value ? '1' : '0');

/** Strip dashes so an ISO `YYYY-MM-DD` becomes GTFS `YYYYMMDD`. Idempotent. */
export const toGtfsDate = (value: string): string => value.replace(/-/g, '').trim();

/** Hex colour without a leading `#`, upper-cased; undefined when invalid. */
const toGtfsColor = (color?: string): string | undefined => {
  if (!color) return undefined;
  const cleaned = color.trim().replace(/^#/, '');
  return /^[0-9a-f]{6}$/i.test(cleaned) ? cleaned.toUpperCase() : undefined;
};

/** Parse `HH:mm` (or `HH:mm:ss`) to minutes since midnight. */
const clockToMinutes = (time: string): number => {
  const parts = time.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? '0');
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
};

/**
 * Format absolute minutes-since-service-midnight as a GTFS `HH:MM:SS` time.
 * Hours may exceed 24 (e.g. `25:15:00`) for after-midnight service — this is
 * required by the spec and is exactly why we cannot reuse the wrapping
 * `HH:mm` output of `generateTimetable`.
 */
export const formatGtfsTime = (totalMinutes: number): string => {
  const m = Math.max(0, Math.round(totalMinutes));
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
};

/** Sanitise an id for GTFS (collapse whitespace); ensures uniqueness via a registry. */
const makeUniqueId = (base: string, used: Set<string>, fallback: string): string => {
  let candidate = (base || '').trim().replace(/\s+/g, '_') || fallback;
  if (!used.has(candidate)) {
    used.add(candidate);
    return candidate;
  }
  let n = 2;
  while (used.has(`${candidate}_${n}`)) n += 1;
  candidate = `${candidate}_${n}`;
  used.add(candidate);
  return candidate;
};

const slugify = (value: string, fallback: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || fallback;

// ---------------------------------------------------------------------------
// Trip synthesis
// ---------------------------------------------------------------------------

/** One stop within a synthesised/expanded trip, in absolute minutes from service midnight. */
interface TripStop {
  /** Index into `route.stops` (so we can resolve the stop_id). */
  stopConfigIndex: number;
  arrivalMin: number;
  departureMin: number;
}

interface SynthTrip {
  directionId: number;
  stops: TripStop[];
}

const directionIdForRoute = (route: FixedRoute): number =>
  route.directionId === 'inbound' ? 1 : 0;

const findSegmentMinutes = (
  route: FixedRoute,
  fromStopId: string,
  toStopId: string,
  multiplier: number,
): number | null => {
  const direct = route.segments.find((s) => s.fromStopId === fromStopId && s.toStopId === toStopId);
  const reverse = route.segments.find((s) => s.fromStopId === toStopId && s.toStopId === fromStopId);
  const segment = direct ?? reverse;
  return segment ? Math.round(segment.travelTimeMinutes * multiplier) : null;
};

/** Per-stop offsets (minutes from trip start) for the forward direction. */
const forwardOffsets = (route: FixedRoute, multiplier: number): TripStop[] => {
  const out: TripStop[] = [];
  let t = 0;
  route.stops.forEach((stopConfig, i) => {
    if (i > 0) {
      const seg = findSegmentMinutes(route, route.stops[i - 1].stopId, stopConfig.stopId, multiplier);
      if (seg !== null) t += seg;
    }
    const arrivalMin = t;
    const departureMin = arrivalMin + (stopConfig.waitTimeMinutes || 0);
    out.push({ stopConfigIndex: i, arrivalMin, departureMin });
    t = departureMin;
  });
  return out;
};

/** Per-stop offsets for the reverse return journey (stops walked last→first). */
const reverseOffsets = (route: FixedRoute, multiplier: number): TripStop[] => {
  const out: TripStop[] = [];
  let t = 0;
  for (let i = route.stops.length - 1; i >= 0; i -= 1) {
    const stopConfig = route.stops[i];
    if (i < route.stops.length - 1) {
      const seg = findSegmentMinutes(route, route.stops[i + 1].stopId, stopConfig.stopId, multiplier);
      if (seg !== null) t += seg;
    }
    const arrivalMin = t;
    const departureMin = arrivalMin + (stopConfig.waitTimeMinutes || 0);
    out.push({ stopConfigIndex: i, arrivalMin, departureMin });
    t = departureMin;
  }
  return out;
};

const offsetTrip = (offsets: TripStop[], startMin: number, directionId: number): SynthTrip => ({
  directionId,
  stops: offsets.map((o) => ({
    stopConfigIndex: o.stopConfigIndex,
    arrivalMin: startMin + o.arrivalMin,
    departureMin: startMin + o.departureMin,
  })),
});

/**
 * Build trips from imported `tripSchedules`. We use each schedule's
 * `startTimeMinutes` (the first stop's DEPARTURE) as the anchor and lay the
 * route's own segment/wait offsets over it, rather than the schedule's
 * per-stop `stopArrivalTimesMinutes` — those are stored relative to the first
 * departure and so wrap the first stop's (earlier) arrival to ~24h. Anchoring
 * `departure[0] = startTimeMinutes` reproduces the original feed's times.
 */
const tripsFromSchedules = (route: FixedRoute, multiplier: number): SynthTrip[] => {
  const directionId = directionIdForRoute(route);
  const offsets = forwardOffsets(route, multiplier);
  const firstDeparture = offsets[0]?.departureMin ?? 0;
  return (route.tripSchedules || []).map((schedule) => ({
    directionId,
    stops: offsets.map((o) => ({
      stopConfigIndex: o.stopConfigIndex,
      arrivalMin: schedule.startTimeMinutes + (o.arrivalMin - firstDeparture),
      departureMin: schedule.startTimeMinutes + (o.departureMin - firstDeparture),
    })),
  }));
};

/**
 * Synthesise trips for an authored route across its service window, mirroring
 * the loop/vehicle/return logic of `generateTimetable` but in absolute minutes.
 */
const synthesiseTrips = (route: FixedRoute, multiplier: number): SynthTrip[] => {
  if (route.stops.length < 2) return [];

  const trips: SynthTrip[] = [];
  const fwd = forwardOffsets(route, multiplier);
  const forwardLastDeparture = fwd[fwd.length - 1].departureMin;

  const serviceStart = clockToMinutes(route.startTime);
  let serviceEnd = route.endTime ? clockToMinutes(route.endTime) : DEFAULT_END_HOUR * 60;
  if (serviceEnd <= serviceStart) serviceEnd += 24 * 60;

  // Return travel from last stop back to first (used to schedule the next loop).
  const lastStopId = route.stops[route.stops.length - 1].stopId;
  const firstStopId = route.stops[0].stopId;
  const explicitReturn = findSegmentMinutes(route, lastStopId, firstStopId, multiplier);

  if (route.isCirculator) {
    const returnTravel =
      explicitReturn ??
      (route.segments.length > 0 ? Math.round(route.segments[0].travelTimeMinutes * multiplier) : 0);
    const loopDuration = forwardLastDeparture + returnTravel;
    const vehicleCount = route.vehicleCount && route.vehicleCount > 1 ? route.vehicleCount : 1;
    const headway = loopDuration > 0 ? loopDuration / vehicleCount : 0;

    for (let v = 0; v < vehicleCount; v += 1) {
      let loopStart = serviceStart + Math.round(v * headway);
      let loops = 0;
      while (loopStart < serviceEnd && loops < MAX_LOOPS) {
        trips.push(offsetTrip(fwd, loopStart, directionIdForRoute(route)));
        loopStart += loopDuration > 0 ? loopDuration : serviceEnd; // avoid infinite loop on 0 duration
        loops += 1;
      }
    }
    return trips;
  }

  // Non-circulator: forward trips, optionally with reverse-return trips between.
  const rev = route.reverseReturnJourney ? reverseOffsets(route, multiplier) : null;
  const returnTravelFallback =
    explicitReturn ?? Math.round(fwd[fwd.length - 1].arrivalMin * 0.5);
  const LAYOVER = 5;

  let tripStart = serviceStart;
  let loops = 0;
  while (tripStart < serviceEnd && loops < MAX_LOOPS) {
    trips.push(offsetTrip(fwd, tripStart, 0));

    const forwardComplete = tripStart + forwardLastDeparture;
    let returnComplete: number;
    if (rev) {
      const reverseTrip = offsetTrip(rev, forwardComplete, 1);
      trips.push(reverseTrip);
      returnComplete = reverseTrip.stops[reverseTrip.stops.length - 1].departureMin;
    } else {
      returnComplete = forwardComplete + returnTravelFallback;
    }

    tripStart = returnComplete + LAYOVER;
    loops += 1;
  }
  return trips;
};

const buildTripsForRoute = (route: FixedRoute, multiplier: number): SynthTrip[] => {
  if (route.tripSchedules && route.tripSchedules.length > 0) {
    return tripsFromSchedules(route, multiplier);
  }
  return synthesiseTrips(route, multiplier);
};

// ---------------------------------------------------------------------------
// Calendar dedup
// ---------------------------------------------------------------------------

interface ResolvedCalendar {
  serviceId: string;
  days: ServiceDaysMask;
  startDate: string; // YYYYMMDD
  endDate: string; // YYYYMMDD
  exceptions: Array<{ date: string; type: 'added' | 'removed' }>;
}

const calendarSignature = (cal: Omit<ResolvedCalendar, 'serviceId'>): string => {
  const days = DAY_KEYS.map((k) => bool01(cal.days[k])).join('');
  const exceptions = [...cal.exceptions]
    .map((e) => `${toGtfsDate(e.date)}:${e.type}`)
    .sort()
    .join(',');
  return `${days}|${cal.startDate}|${cal.endDate}|${exceptions}`;
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** A blocking reason, or null if the route is exportable. */
const blockingReason = (route: FixedRoute, stopMap: Map<string, FixedRouteStop>): string | null => {
  if (route.stops.length < 2) return 'Route has fewer than 2 stops';
  if (route.segments.length !== route.stops.length - 1) {
    return `Segment count (${route.segments.length}) does not equal stops − 1 (${route.stops.length - 1})`;
  }
  const missing = route.stops.find((sc) => !stopMap.has(sc.stopId));
  if (missing) return `References unknown stop ${missing.stopId}`;
  if (!(route.name?.trim() || route.shortName?.trim())) return 'Route has no name';
  return null;
};

// ---------------------------------------------------------------------------
// buildGtfsFeed
// ---------------------------------------------------------------------------

export const buildGtfsFeed = (
  state: FixedRoutesState,
  options: GtfsExportOptions,
): GtfsExportResult => {
  const warnings: string[] = [];
  const skippedRoutes: SkippedRoute[] = [];
  const defaultRouteType = options.defaultRouteType ?? DEFAULT_GTFS_ROUTE_TYPE;

  const selectedIds = options.selectedRouteIds && options.selectedRouteIds.length > 0
    ? new Set(options.selectedRouteIds)
    : null;
  const selectedRoutes = state.routes.filter((r) => !selectedIds || selectedIds.has(r.id));

  const stopMap = new Map<string, FixedRouteStop>(state.stops.map((s) => [s.id, s]));

  // --- Default agency (for authored routes) -------------------------------
  const defaultAgency: GtfsAgency = {
    agencyId: slugify(options.meta.agencyName, 'atlas'),
    name: options.meta.agencyName,
    url: options.meta.agencyUrl,
    timezone: options.meta.agencyTimezone,
    lang: options.meta.agencyLang || undefined,
  };

  // --- Registries ---------------------------------------------------------
  const usedRouteIds = new Set<string>();
  const usedStopIds = new Set<string>();
  const usedTripIds = new Set<string>();
  const emittedRouteRows = new Set<string>();
  const agenciesById = new Map<string, GtfsAgency>();
  const calendarBySignature = new Map<string, ResolvedCalendar>();
  const includedStopRefs = new Set<string>(); // FixedRouteStop.id values actually used

  const stopIdByRef = new Map<string, string>(); // FixedRouteStop.id → gtfs stop_id

  const resolveStopId = (stop: FixedRouteStop): string => {
    const existing = stopIdByRef.get(stop.id);
    if (existing) return existing;
    let base = stop.uatStopId?.trim();
    if (!base) base = stop.id.startsWith('gtfs-stop-') ? stop.id.slice('gtfs-stop-'.length) : stop.id;
    const id = makeUniqueId(base, usedStopIds, stop.id);
    stopIdByRef.set(stop.id, id);
    return id;
  };

  /**
   * Prefer the preserved `gtfsRouteId` so outbound/inbound siblings coalesce onto
   * one routes.txt row. Strip a `-d0`/`-d1` import id suffix when recovering the
   * base from `route.id`.
   */
  const resolveRouteId = (route: FixedRoute): string => {
    const preferred = route.gtfsRouteId?.trim();
    if (preferred) {
      usedRouteIds.add(preferred);
      return preferred;
    }
    let base = route.id.startsWith('gtfs-route-') ? route.id.slice('gtfs-route-'.length) : route.id;
    base = base.replace(/-d[01]$/, '');
    return makeUniqueId(base, usedRouteIds, route.id);
  };

  const stripDirectionNameSuffix = (name: string): string =>
    name.replace(/\s*\((?:Outbound|Inbound)\)\s*$/i, '').trim();

  const resolveAgency = (route: FixedRoute): GtfsAgency => {
    const agency = route.gtfsAgency && route.gtfsAgency.name ? route.gtfsAgency : defaultAgency;
    if (!agenciesById.has(agency.agencyId)) {
      agenciesById.set(agency.agencyId, agency);
    }
    return agency;
  };

  const resolveCalendar = (route: FixedRoute): ResolvedCalendar => {
    const days = route.serviceDays ?? options.defaultServiceDays;
    const range = route.serviceDateRange ?? options.defaultServiceDateRange;
    const exceptions = (route.serviceExceptions ?? []).map((e) => ({
      date: toGtfsDate(e.date),
      type: e.type,
    }));
    const partial = {
      days,
      startDate: toGtfsDate(range.startDate),
      endDate: toGtfsDate(range.endDate),
      exceptions,
    };
    const signature = calendarSignature(partial);
    const existing = calendarBySignature.get(signature);
    if (existing) return existing;
    const resolved: ResolvedCalendar = { serviceId: `svc_${calendarBySignature.size + 1}`, ...partial };
    calendarBySignature.set(signature, resolved);
    return resolved;
  };

  // --- Output tables ------------------------------------------------------
  const routesOut: GTFSRoutesRow[] = [];
  const tripsOut: GTFSTripRow[] = [];
  const stopTimesOut: GTFSStopTimeRow[] = [];
  const shapesOut: GTFSShapeRow[] = [];

  selectedRoutes.forEach((route) => {
    const reason = blockingReason(route, stopMap);
    if (reason) {
      skippedRoutes.push({ routeId: route.id, routeName: route.name || route.id, reason });
      return;
    }

    const multiplier = route.trafficMultiplier ?? state.trafficMultiplier ?? 1;
    const trips = buildTripsForRoute(route, multiplier);
    if (trips.length === 0) {
      skippedRoutes.push({
        routeId: route.id,
        routeName: route.name || route.id,
        reason: 'No trips could be generated (check start/end time and schedules)',
      });
      return;
    }

    const routeId = resolveRouteId(route);
    const agency = resolveAgency(route);
    const calendar = resolveCalendar(route);
    const gtfsDirectionId = directionIdForRoute(route);

    // shapes.txt — unique per FixedRoute direction so outbound/inbound don't overwrite
    let shapeId: string | undefined;
    const coords = route.geometry?.coordinates;
    if (coords && coords.length > 1) {
      shapeId = `shape_${routeId}_d${gtfsDirectionId}`;
      coords.forEach(([lat, lng], i) => {
        shapesOut.push({
          shape_id: shapeId as string,
          shape_pt_lat: String(lat),
          shape_pt_lon: String(lng),
          shape_pt_sequence: String(i),
        });
      });
    } else {
      warnings.push(`Route "${route.name}" has no geometry; exported without a shape.`);
    }

    // routes.txt — one row per gtfs route_id (direction siblings coalesce)
    if (!emittedRouteRows.has(routeId)) {
      emittedRouteRows.add(routeId);
      const longName = stripDirectionNameSuffix(route.name?.trim() || '');
      const shortName = route.shortName?.trim() || '';
      routesOut.push({
        route_id: routeId,
        agency_id: agency.agencyId,
        route_short_name: shortName || undefined,
        route_long_name: longName || undefined,
        route_desc: route.routeDesc?.trim() || undefined,
        route_type: String(route.gtfsRouteType ?? defaultRouteType),
        route_color: toGtfsColor(route.color),
        route_text_color: toGtfsColor(route.routeTextColor),
      });
    }

    // Default headsign = last stop's name.
    const lastStop = stopMap.get(route.stops[route.stops.length - 1].stopId);
    const defaultHeadsign = route.tripHeadsign?.trim() || lastStop?.name || undefined;

    trips.forEach((trip, tripIndex) => {
      const tripId = makeUniqueId(
        `${routeId}_d${trip.directionId}_t${tripIndex + 1}`,
        usedTripIds,
        `${routeId}_t${tripIndex + 1}`,
      );
      tripsOut.push({
        trip_id: tripId,
        route_id: routeId,
        service_id: calendar.serviceId,
        shape_id: shapeId,
        trip_headsign: defaultHeadsign,
        direction_id: String(trip.directionId),
      });

      trip.stops.forEach((tripStop, sequence) => {
        const stop = stopMap.get(route.stops[tripStop.stopConfigIndex].stopId);
        if (!stop) return;
        includedStopRefs.add(stop.id);
        stopTimesOut.push({
          trip_id: tripId,
          arrival_time: formatGtfsTime(tripStop.arrivalMin),
          departure_time: formatGtfsTime(tripStop.departureMin),
          stop_id: resolveStopId(stop),
          stop_sequence: String(sequence),
        });
      });
    });
  });

  // --- stops.txt (only referenced stops) ----------------------------------
  const stopsOut: GTFSStopsRow[] = [];
  state.stops.forEach((stop) => {
    if (!includedStopRefs.has(stop.id)) return;
    stopsOut.push({
      stop_id: resolveStopId(stop),
      stop_name: stop.name,
      stop_lat: String(stop.coordinates.lat),
      stop_lon: String(stop.coordinates.lng),
      stop_desc: stop.description || stop.address || undefined,
    });
  });

  // --- agency.txt ---------------------------------------------------------
  const agencyOut: GTFSAgencyRow[] = Array.from(agenciesById.values()).map((a) => ({
    agency_id: a.agencyId,
    agency_name: a.name,
    agency_url: a.url,
    agency_timezone: a.timezone,
    agency_lang: a.lang,
  }));

  // --- calendar.txt + calendar_dates.txt ----------------------------------
  const calendars = Array.from(calendarBySignature.values());
  const calendarOut: GTFSCalendarRow[] = calendars.map((cal) => ({
    service_id: cal.serviceId,
    monday: bool01(cal.days.monday),
    tuesday: bool01(cal.days.tuesday),
    wednesday: bool01(cal.days.wednesday),
    thursday: bool01(cal.days.thursday),
    friday: bool01(cal.days.friday),
    saturday: bool01(cal.days.saturday),
    sunday: bool01(cal.days.sunday),
    start_date: cal.startDate,
    end_date: cal.endDate,
  }));
  const calendarDatesOut: GTFSCalendarDateRow[] = [];
  calendars.forEach((cal) => {
    cal.exceptions.forEach((e) => {
      calendarDatesOut.push({
        service_id: cal.serviceId,
        date: e.date,
        exception_type: e.type === 'removed' ? '2' : '1',
      });
    });
  });

  // --- feed_info.txt ------------------------------------------------------
  const allStart = calendars.map((c) => c.startDate).filter(Boolean).sort()[0];
  const allEnd = calendars.map((c) => c.endDate).filter(Boolean).sort().slice(-1)[0];
  const feedInfo: GTFSFeedInfoRow[] = [
    {
      feed_publisher_name: options.meta.feedName || options.meta.agencyName,
      feed_publisher_url: options.meta.feedPublisherUrl || options.meta.agencyUrl,
      feed_lang: options.meta.agencyLang || 'en',
      feed_version: options.meta.feedVersion,
      feed_start_date: allStart,
      feed_end_date: allEnd,
    },
  ];

  if (routesOut.length === 0) {
    warnings.push('No exportable routes — the feed contains no routes/trips.');
  }

  return {
    feed: {
      agency: agencyOut,
      stops: stopsOut,
      routes: routesOut,
      trips: tripsOut,
      stopTimes: stopTimesOut,
      calendar: calendarOut,
      calendarDates: calendarDatesOut,
      shapes: shapesOut,
      feedInfo,
    },
    warnings,
    skippedRoutes,
    counts: {
      routes: routesOut.length,
      trips: tripsOut.length,
      stops: stopsOut.length,
      calendars: calendarOut.length,
      agencies: agencyOut.length,
    },
  };
};

// ---------------------------------------------------------------------------
// writeGtfsZip
// ---------------------------------------------------------------------------

/** Explicit column order per file (stable output, guaranteed headers). */
const FILE_COLUMNS: Record<string, string[]> = {
  'agency.txt': ['agency_id', 'agency_name', 'agency_url', 'agency_timezone', 'agency_lang'],
  'stops.txt': ['stop_id', 'stop_name', 'stop_lat', 'stop_lon', 'stop_desc'],
  'routes.txt': [
    'route_id',
    'agency_id',
    'route_short_name',
    'route_long_name',
    'route_desc',
    'route_type',
    'route_color',
    'route_text_color',
  ],
  'trips.txt': ['route_id', 'service_id', 'trip_id', 'trip_headsign', 'direction_id', 'shape_id'],
  'stop_times.txt': ['trip_id', 'arrival_time', 'departure_time', 'stop_id', 'stop_sequence'],
  'calendar.txt': [
    'service_id',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
    'start_date',
    'end_date',
  ],
  'calendar_dates.txt': ['service_id', 'date', 'exception_type'],
  'shapes.txt': ['shape_id', 'shape_pt_lat', 'shape_pt_lon', 'shape_pt_sequence'],
  'feed_info.txt': [
    'feed_publisher_name',
    'feed_publisher_url',
    'feed_lang',
    'feed_version',
    'feed_start_date',
    'feed_end_date',
  ],
};

const feedTable = (feed: GtfsExportFeed, fileName: string): Record<string, unknown>[] => {
  switch (fileName) {
    case 'agency.txt':
      return feed.agency as unknown as Record<string, unknown>[];
    case 'stops.txt':
      return feed.stops as unknown as Record<string, unknown>[];
    case 'routes.txt':
      return feed.routes as unknown as Record<string, unknown>[];
    case 'trips.txt':
      return feed.trips as unknown as Record<string, unknown>[];
    case 'stop_times.txt':
      return feed.stopTimes as unknown as Record<string, unknown>[];
    case 'calendar.txt':
      return feed.calendar as unknown as Record<string, unknown>[];
    case 'calendar_dates.txt':
      return feed.calendarDates as unknown as Record<string, unknown>[];
    case 'shapes.txt':
      return feed.shapes as unknown as Record<string, unknown>[];
    case 'feed_info.txt':
      return feed.feedInfo as unknown as Record<string, unknown>[];
    default:
      return [];
  }
};

/**
 * Serialise a feed to a `{ filename → CSV }` map. `calendar_dates.txt` and
 * `shapes.txt` are omitted when empty (a valid, smaller feed). Exposed
 * separately from `writeGtfsZip` so the CSV output is unit-testable without a
 * zip/Blob round-trip.
 */
export const serializeGtfsFeed = async (feed: GtfsExportFeed): Promise<Record<string, string>> => {
  const Papa = (await import('papaparse')).default;
  const out: Record<string, string> = {};
  GTFS_EXPORT_FILES.forEach((fileName) => {
    const rows = feedTable(feed, fileName);
    if ((fileName === 'calendar_dates.txt' || fileName === 'shapes.txt') && rows.length === 0) {
      return;
    }
    out[fileName] = Papa.unparse({ fields: FILE_COLUMNS[fileName], data: rows }, { newline: '\n' });
  });
  return out;
};

/**
 * Serialise a feed to CSV-per-file in a GTFS `.zip` (returns a Blob ready for
 * `downloadFile`).
 */
export const writeGtfsZip = async (feed: GtfsExportFeed): Promise<Blob> => {
  const JSZip = (await import('jszip')).default;
  const files = await serializeGtfsFeed(feed);
  const zip = new JSZip();
  Object.entries(files).forEach(([fileName, csv]) => {
    zip.file(fileName, csv);
  });
  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
};
